import useCustomHook from '@/hooks/useCustomHook';
import * as Sentry from '@sentry/nextjs';
import { useContext, useEffect, useRef } from 'react';
import { GameAssetsContext } from '@/shared/GameComponents/GameAssetsContext/GameAssetsContext';

const markAsEndedTimeout = 6000;

export default function useQuestionAudio({ audioId, audio, onPlay, onEnded }) {
  const gameAssetsContext = useContext(GameAssetsContext);
  const fallbackOnEndedRef = useRef(null);
  const playingStateRef = useRef(false);

  const { state, actions } = useCustomHook({
    defaultState: {
      playing: false,
      loading: false,
    },
    actions: {
      play: async () => {
        const audioAsset = gameAssetsContext?.state?.sounds[audioId];
        const audioSrc = audioAsset?.audio || audio;
        try {
          await playAsAudioObject(audioSrc, actions.onEnded);
        } catch (error) {
          console.error('Error playing audio using Audio object method', error);
          Sentry.captureException(error, {
            extra: {
              error: 'Error playing audio using Audio object method',
              audioSrc: audio,
            },
          });

          try {
            await playAudioWithAudioContext(audioSrc, actions.onEnded);
          } catch (error) {
            console.error(
              'Error playing audio using AudioContext method',
              error
            );
            Sentry.captureException(error, {
              extra: {
                error: 'Error playing audio using AudioContext method',
                audioSrc: audio,
              },
            });
          }
        }

        actions.setState({ playing: true });

        fallbackOnEndedRef.current = setTimeout(() => {
          const playing = playingStateRef.current;

          if (playing) {
            actions.onEnded();
          }
        }, markAsEndedTimeout);

        if (onPlay) {
          onPlay();
        }
      },
      onEnded: () => {
        actions.setState({ playing: false });
        if (onEnded) {
          onEnded();
        }
      },
    },
  });

  useEffect(() => {
    return () => {
      if (fallbackOnEndedRef.current) {
        clearTimeout(fallbackOnEndedRef.current);
      }
    };
  }, []);

  useEffect(() => {
    playingStateRef.current = state.playing;
  }, [state.playing]);

  return { state, actions };
}

function playAsAudioObject(audioSrc, onEnded) {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.src = audioSrc;
    audio.oncanplaythrough = () => {
      audio.play();
      resolve(audio);
    };

    audio.onerror = (error) => {
      reject(error);
    };

    audio.onended = onEnded;

    audio.load();
  });
}

async function playAudioWithAudioContext(audioSrc, onEnded) {
  try {
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();

    const response = await fetch(audioSrc);
    const audioData = await response.arrayBuffer();

    const source = audioContext.createBufferSource();
    source.buffer = await audioContext.decodeAudioData(audioData);
    source.connect(audioContext.destination);
    source.onended = onEnded;
    source.start(0);
    return source;
  } catch (error) {
    throw error;
  }
}
